import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Box,
  Grid,
  Paper,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  Star,
  LocationOn,
  Email,
  Phone,
  Close,
  Check,
  Clear,
  Message,
  Notifications,
  Person,
  ChevronRight,
  FilterList,
  Search,
  ExpandMore,
  Business,
  AttachMoney,
  Schedule,
  VideoCall
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const TalentInterviewDashboard = ({collapsed}) => {
  const [activeTab, setActiveTab] = useState(0); // 0: pending, 1: accepted, 2: declined, 3: all
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseData, setResponseData] = useState({
    availableDates: [],
    availableTimeSlots: [],
    message: '',
    status: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Styled components
  const StyledCard = styled(Card)(({ theme }) => ({
    cursor: 'pointer',
    transition: theme.transitions.create(['box-shadow', 'transform']),
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px)',
    },
  }));

  const UrgencyChip = styled(Chip)(({ urgency, theme }) => {
    const colors = {
      high: { bg: theme.palette.error.light, color: theme.palette.error.dark },
      medium: { bg: theme.palette.warning.light, color: theme.palette.warning.dark },
      low: { bg: theme.palette.success.light, color: theme.palette.success.dark }
    };
    return {
      backgroundColor: colors[urgency]?.bg || theme.palette.grey[200],
      color: colors[urgency]?.color || theme.palette.grey[700],
    };
  });

  const StatusChip = styled(Chip)(({ status, theme }) => {
    const colors = {
      pending: { bg: theme.palette.info.light, color: theme.palette.info.dark },
      accepted: { bg: theme.palette.success.light, color: theme.palette.success.dark },
      declined: { bg: theme.palette.error.light, color: theme.palette.error.dark }
    };
    return {
      backgroundColor: colors[status]?.bg || theme.palette.grey[200],
      color: colors[status]?.color || theme.palette.grey[700],
    };
  });

  // Mock interview requests data
  const [interviewRequests, setInterviewRequests] = useState([
    {
      id: 1,
      managerId: 101,
      managerName: "John Smith",
      managerTitle: "Tech Lead",
      companyName: "TechCorp Inc.",
      companyLogo: "/api/placeholder/50/50",
      projectTitle: "E-commerce Platform Development",
      requestedDates: ["2025-05-20", "2025-05-21", "2025-05-22"],
      requestedTimeSlots: ["10:00 AM - 11:00 AM", "2:00 PM - 3:00 PM", "3:00 PM - 4:00 PM"],
      message: "We're looking for a senior frontend developer to join our team for an exciting e-commerce project. Your React expertise would be a great fit!",
      status: "pending",
      createdAt: "2025-05-19",
      urgency: "high",
      estimatedDuration: "1 hour",
      interviewType: "Video Call",
      compensation: "$85-95/hour"
    },
    {
      id: 2,
      managerId: 102,
      managerName: "Sarah Johnson",
      managerTitle: "Product Manager",
      companyName: "StartupXYZ",
      companyLogo: "/api/placeholder/50/50",
      projectTitle: "Mobile App Development",
      requestedDates: ["2025-05-23", "2025-05-24"],
      requestedTimeSlots: ["9:00 AM - 10:00 AM", "11:00 AM - 12:00 PM"],
      message: "Hi! We're impressed by your profile and would love to discuss a mobile app project with you.",
      status: "pending",
      createdAt: "2025-05-18",
      urgency: "medium",
      estimatedDuration: "45 minutes",
      interviewType: "Phone Call",
      compensation: "$80/hour"
    },
    {
      id: 3,
      managerId: 103,
      managerName: "Mike Chen",
      managerTitle: "Engineering Manager",
      companyName: "DataFlow Solutions",
      companyLogo: "/api/placeholder/50/50",
      projectTitle: "Dashboard Redesign",
      requestedDates: ["2025-05-21"],
      requestedTimeSlots: ["1:00 PM - 2:00 PM"],
      message: "Looking for UI/UX expertise for our analytics dashboard project.",
      status: "accepted",
      createdAt: "2025-05-17",
      urgency: "low",
      estimatedDuration: "30 minutes",
      interviewType: "In-person",
      compensation: "$90/hour",
      respondedAt: "2025-05-18",
      selectedDate: "2025-05-21",
      selectedTimeSlot: "1:00 PM - 2:00 PM",
      responseMessage: "I'm excited about this opportunity! Looking forward to discussing the project."
    },
    {
      id: 4,
      managerId: 104,
      managerName: "Emma Wilson",
      managerTitle: "CTO",
      companyName: "FinanceFlow",
      companyLogo: "/api/placeholder/50/50",
      projectTitle: "Backend Infrastructure",
      requestedDates: ["2025-05-20"],
      requestedTimeSlots: ["4:00 PM - 5:00 PM"],
      message: "We need expertise in scalable backend architecture.",
      status: "declined",
      createdAt: "2025-05-16",
      urgency: "high",
      estimatedDuration: "1 hour",
      interviewType: "Video Call",
      compensation: "$100/hour",
      respondedAt: "2025-05-17",
      responseMessage: "Thank you for the opportunity, but I'm fully booked for the next month."
    }
  ]);

  const timeSlots = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "1:00 PM - 2:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
    "4:00 PM - 5:00 PM"
  ];

  const tabConfig = ['pending', 'accepted', 'declined', 'all'];

  // Get counts for different statuses
  const getStatusCounts = () => ({
    pending: interviewRequests.filter(req => req.status === 'pending').length,
    accepted: interviewRequests.filter(req => req.status === 'accepted').length,
    declined: interviewRequests.filter(req => req.status === 'declined').length,
    all: interviewRequests.length
  });

  // Filter requests based on active tab and search
  const filteredRequests = interviewRequests.filter(request => {
    const statusFilter = activeTab === 3 ? true : request.status === tabConfig[activeTab];
    const searchFilter = searchTerm === '' || 
      request.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.managerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.projectTitle.toLowerCase().includes(searchTerm.toLowerCase());
    return statusFilter && searchFilter;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
  };

  const handleRespond = (status) => {
    setResponseData(prev => ({ ...prev, status }));
    setShowResponseModal(true);
  };

  const handleDateSelection = (date) => {
    setResponseData(prev => ({
      ...prev,
      availableDates: prev.availableDates.includes(date)
        ? prev.availableDates.filter(d => d !== date)
        : [...prev.availableDates, date]
    }));
  };

  const handleTimeSlotSelection = (slot) => {
    setResponseData(prev => ({
      ...prev,
      availableTimeSlots: prev.availableTimeSlots.includes(slot)
        ? prev.availableTimeSlots.filter(s => s !== slot)
        : [...prev.availableTimeSlots, slot]
    }));
  };

  const submitResponse = () => {
    if (responseData.status === 'accepted' && 
        (responseData.availableDates.length === 0 || responseData.availableTimeSlots.length === 0)) {
      setSnackbar({
        open: true,
        message: 'Please select at least one date and time slot when accepting an interview',
        severity: 'error'
      });
      return;
    }

    // Update the request status
    setInterviewRequests(prev => prev.map(request => 
      request.id === selectedRequest.id
        ? {
            ...request,
            status: responseData.status,
            respondedAt: new Date().toISOString().split('T')[0],
            responseMessage: responseData.message,
            ...(responseData.status === 'accepted' && {
              selectedDate: responseData.availableDates[0],
              selectedTimeSlot: responseData.availableTimeSlots[0]
            })
          }
        : request
    ));

    // Reset states
    setShowResponseModal(false);
    setSelectedRequest(null);
    setResponseData({
      availableDates: [],
      availableTimeSlots: [],
      message: '',
      status: ''
    });

    setSnackbar({
      open: true,
      message: `Interview request ${responseData.status} successfully!`,
      severity: 'success'
    });
  };

  const getNextSevenDays = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i <= 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const statusCounts = getStatusCounts();

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh',  maxWidth: '100%',
  marginLeft: collapsed ? "60px" : "240px", }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Interview Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Manage your interview opportunities
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={statusCounts.pending} color="error">
              <Notifications />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {selectedRequest ? (
          /* Detailed Request View */
          <Card elevation={2}>
            <CardContent sx={{ p: 3 }}>
              {/* Back Button */}
              <Button
                startIcon={<ChevronRight sx={{ transform: 'rotate(180deg)' }} />}
                onClick={() => setSelectedRequest(null)}
                sx={{ mb: 3 }}
              >
                Back to requests
              </Button>

              {/* Request Header */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item>
                    <Avatar
                      src={selectedRequest.companyLogo}
                      alt={selectedRequest.companyName}
                      sx={{ width: 64, height: 64 }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h4" gutterBottom fontWeight={600}>
                      {selectedRequest.projectTitle}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {selectedRequest.companyName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRequest.managerName} • {selectedRequest.managerTitle}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Stack direction="row" spacing={1}>
                      <UrgencyChip
                        urgency={selectedRequest.urgency}
                        label={`${selectedRequest.urgency} Priority`}
                        size="small"
                      />
                      <StatusChip
                        status={selectedRequest.status}
                        label={selectedRequest.status}
                        size="small"
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Request Details */}
              <Grid container spacing={4}>
                <Grid item xs={12} lg={6}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Project Details
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <AttachMoney fontSize="small" color="action" />
                          <Typography variant="body2">Compensation:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {selectedRequest.compensation}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <VideoCall fontSize="small" color="action" />
                          <Typography variant="body2">Interview Type:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedRequest.interviewType}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2">Duration:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedRequest.estimatedDuration}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">Request Date:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDate(selectedRequest.createdAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Manager's Message
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {selectedRequest.message}
                      </Typography>
                    </Paper>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Requested Interview Slots
                  </Typography>
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Preferred Dates:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selectedRequest.requestedDates.map((date, index) => (
                        <Chip
                          key={index}
                          label={formatDate(date)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Preferred Times:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selectedRequest.requestedTimeSlots.map((slot, index) => (
                        <Chip
                          key={index}
                          label={slot}
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>

                  {selectedRequest.status === 'accepted' && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Confirmed Interview
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CalendarToday fontSize="small" />
                        <Typography variant="body2">
                          Date: {formatDate(selectedRequest.selectedDate)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AccessTime fontSize="small" />
                        <Typography variant="body2">
                          Time: {selectedRequest.selectedTimeSlot}
                        </Typography>
                      </Box>
                      {selectedRequest.responseMessage && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
                          Your message: "{selectedRequest.responseMessage}"
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {selectedRequest.status === 'declined' && selectedRequest.responseMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Your Response
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{selectedRequest.responseMessage}"
                      </Typography>
                    </Alert>
                  )}
                </Grid>
              </Grid>

              {/* Action Buttons */}
              {selectedRequest.status === 'pending' && (
                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 4 }}>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Check />}
                      onClick={() => handleRespond('accepted')}
                      size="large"
                    >
                      Accept Interview
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Clear />}
                      onClick={() => handleRespond('declined')}
                      size="large"
                    >
                      Decline Interview
                    </Button>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Requests List View */
          <Box>
            {/* Filters and Search */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.pending} color="error">
                          Pending
                        </Badge>
                      }
                    />
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.accepted} color="success">
                          Accepted
                        </Badge>
                      }
                    />
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.declined} color="error" variant="dot">
                          Declined
                        </Badge>
                      }
                    />
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.all} color="primary">
                          All
                        </Badge>
                      }
                    />
                  </Tabs>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by company, manager, or project..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Requests List */}
            {filteredRequests.length === 0 ? (
              <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                <Message sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Interview Requests
                </Typography>
                <Typography color="text.secondary">
                  {activeTab === 0 
                    ? "You don't have any pending interview requests."
                    : searchTerm 
                      ? "No requests match your search criteria."
                      : `You don't have any ${tabConfig[activeTab]} interview requests.`
                  }
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {filteredRequests.map((request) => (
                  <StyledCard
                    key={request.id}
                    onClick={() => handleRequestClick(request)}
                    elevation={1}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <Avatar
                            src={request.companyLogo}
                            alt={request.companyName}
                            sx={{ width: 48, height: 48 }}
                          />
                        </Grid>
                        <Grid item xs>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {request.projectTitle}
                              </Typography>
                              <Typography color="text.secondary" gutterBottom>
                                {request.companyName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {request.managerName} • {request.managerTitle}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <UrgencyChip
                                urgency={request.urgency}
                                label={request.urgency}
                                size="small"
                              />
                              <StatusChip
                                status={request.status}
                                label={request.status}
                                size="small"
                              />
                            </Stack>
                          </Box>
                          
                          <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {request.requestedDates.length} date{request.requestedDates.length > 1 ? 's' : ''} available
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {request.estimatedDuration}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {request.compensation}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {request.message.substring(0, 150)}
                            {request.message.length > 150 && '...'}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <ChevronRight color="action" />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* Response Modal */}
        <Dialog
          open={showResponseModal}
          onClose={() => setShowResponseModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {responseData.status === 'accepted' ? 'Accept' : 'Decline'} Interview Request
              </Typography>
              <IconButton onClick={() => setShowResponseModal(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {responseData.status === 'accepted' && (
              <>
                {/* Available Dates */}
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                    <CalendarToday sx={{ mr: 1 }} />
                    Select Your Available Dates
                  </Typography>
                  <Grid container spacing={1}>
                    {getNextSevenDays().map((date) => (
                      <Grid item xs={6} sm={4} key={date}>
                        <ToggleButton
                          fullWidth
                          value={date}
                          selected={responseData.availableDates.includes(date)}
                          onChange={() => handleDateSelection(date)}
                          disabled={!selectedRequest.requestedDates.includes(date)}
                          sx={{ flexDirection: 'column', p: 1 }}
                        >
                          <Typography variant="body2">
                            {formatDate(date)}
                          </Typography>
                          {selectedRequest.requestedDates.includes(date) && (
                            <Typography variant="caption" color="primary">
                              Requested
                            </Typography>
                          )}
                        </ToggleButton>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Available Time Slots */}
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                    <AccessTime sx={{ mr: 1 }} />
                    Select Your Available Time Slots
                  </Typography>
                  <Grid container spacing={1}>
                    {timeSlots.map((slot) => (
                      <Grid item xs={6} sm={4} key={slot}>
                        <ToggleButton
                          fullWidth
                          value={slot}
                          selected={responseData.availableTimeSlots.includes(slot)}
                          onChange={() => handleTimeSlotSelection(slot)}
                          disabled={!selectedRequest.requestedTimeSlots.includes(slot)}
                          sx={{ flexDirection: 'column', p: 1 }}
                        >
                          <Typography variant="body2">
                            {slot}
                          </Typography>
                          {selectedRequest.requestedTimeSlots.includes(slot) && (
                            <Typography variant="caption" color="primary">
                              Requested
                            </Typography>
                          )}
                        </ToggleButton>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}

            {/* Message */}
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  responseData.status === 'accepted' 
                    ? 'Message to Manager (Optional)' 
                    : 'Reason for Declining (Optional)'
                }
                placeholder={
                  responseData.status === 'accepted'
                    ? "Share your enthusiasm about the opportunity..."
                    : "Provide a polite reason for declining..."
                }
                value={responseData.message}
                onChange={(e) => setResponseData(prev => ({ ...prev, message: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowResponseModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={responseData.status === 'accepted' ? 'success' : 'error'}
              onClick={submitResponse}
              disabled={
                responseData.status === 'accepted' && 
                (responseData.availableDates.length === 0 || responseData.availableTimeSlots.length === 0)
              }
              startIcon={responseData.status === 'accepted' ? <Check /> : <Clear />}
            >
              {responseData.status === 'accepted' ? 'Accept Interview' : 'Decline Interview'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TalentInterviewDashboard;

